import Phaser from "phaser";
import { COLORS, NEUTROPHIL, PATHOGENS } from "../config";
import { SaveState } from "../systems/SaveState";
import type { PathogenDef } from "../types";

/**
 * Generates all placeholder art into the texture cache, then hands off to the
 * menu. Every entity references a texture KEY produced here — when real sprite
 * art arrives, only this scene changes, not the gameplay code.
 */
export default class BootScene extends Phaser.Scene {
  constructor() {
    super("Boot");
  }

  preload(): void {
    // Optional region background images (drop them in public/regions/). Missing
    // files fail softly — RegionBackground falls back to procedural art.
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
      /* ignore: a region without an image just uses the procedural fallback */
    });
    const base = import.meta.env.BASE_URL;
    this.load.image("bg-lungs", `${base}regions/lungs.jpg`);
    this.load.image("bg-intestines", `${base}regions/intestines.jpg`);
  }

  create(): void {
    SaveState.load();

    this.makeSoftCircle("soft", 128);
    this.makeSparkle("sparkle", 24);
    this.makeCell("cell", NEUTROPHIL.radius);
    this.makeVirus("virus", PATHOGENS.virus);
    this.makeBacterium("bacterium", PATHOGENS.bacterium);
    this.makeToxinBlob("toxinCloud", PATHOGENS.toxinCloud);
    this.makeElite("elite", PATHOGENS.elite);
    this.makeToxinBlob("apex", PATHOGENS.apex);
    this.makeShot("shot");
    this.makeEnemyShot("enemy-shot");
    this.makePickup("pickup-plus", COLORS.primaryPickup, "plus");
    this.makePickup("pickup-shield", COLORS.secondaryPickup, "shield");
    this.makePickup("pickup-rapid", COLORS.secondaryPickup, "rapid");
    this.makeRing("joystick-base", 72, 0xffffff, 0.18, 0.0);
    this.makeRing("joystick-thumb", 36, 0xffffff, 0.55, 0.18);

    this.scene.start("Menu");
  }

  // ── Texture builders ───────────────────────────────────────────────────────

  /** Soft white radial gradient (tintable) used for glows, auras, sparkles. */
  private makeSoftCircle(key: string, size: number): void {
    const tex = this.textures.createCanvas(key, size, size);
    if (!tex) return;
    const ctx = tex.getContext();
    const r = size / 2;
    const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.55, "rgba(255,255,255,0.55)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
  }

  private makeSparkle(key: string, size: number): void {
    const tex = this.textures.createCanvas(key, size, size);
    if (!tex) return;
    const ctx = tex.getContext();
    const r = size / 2;
    const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.4, "rgba(255,244,194,0.9)");
    grad.addColorStop(1, "rgba(255,244,194,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.fill();
    tex.refresh();
  }

  /** White blood cell: translucent membrane with a soft glowing core. */
  private makeCell(key: string, radius: number): void {
    const size = radius * 2 + 16;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(COLORS.cell, 0.22);
    g.fillCircle(c, c, radius + 6);
    g.fillStyle(COLORS.cell, 0.45);
    g.fillCircle(c, c, radius);
    g.lineStyle(4, COLORS.cell, 0.85);
    g.strokeCircle(c, c, radius);
    g.fillStyle(COLORS.cellCore, 0.85);
    g.fillCircle(c, c, radius * 0.42);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Spiky virus with googly eyes. */
  private makeVirus(key: string, def: PathogenDef): void {
    const r = def.radius;
    const size = r * 2 + 14;
    const c = size / 2;
    const g = this.add.graphics();
    // Spikes
    g.fillStyle(def.color, 1);
    const spikes = 9;
    for (let i = 0; i < spikes; i++) {
      const a = (i / spikes) * Math.PI * 2;
      g.fillCircle(c + Math.cos(a) * r, c + Math.sin(a) * r, r * 0.28);
    }
    g.fillStyle(def.color, 1);
    g.fillCircle(c, c, r * 0.85);
    this.drawGooglyEyes(g, c, c, r * 0.85);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Rod-shaped bacterium (capsule) with googly eyes. */
  private makeBacterium(key: string, def: PathogenDef): void {
    const r = def.radius;
    const w = r * 2.6;
    const h = r * 1.7;
    const size = Math.ceil(w) + 14;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(def.color, 1);
    g.fillRoundedRect(c - w / 2, c - h / 2, w, h, h / 2);
    g.lineStyle(3, 0x000000, 0.12);
    g.strokeRoundedRect(c - w / 2, c - h / 2, w, h, h / 2);
    this.drawGooglyEyes(g, c, c - h * 0.08, h * 0.85);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Blobby toxin-cloud bacterium with googly eyes (aura drawn separately). */
  private makeToxinBlob(key: string, def: PathogenDef): void {
    const r = def.radius;
    const size = r * 2 + 16;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(def.color, 1);
    // lumpy body
    const lumps = 7;
    for (let i = 0; i < lumps; i++) {
      const a = (i / lumps) * Math.PI * 2;
      g.fillCircle(c + Math.cos(a) * r * 0.5, c + Math.sin(a) * r * 0.5, r * 0.55);
    }
    g.fillCircle(c, c, r * 0.8);
    this.drawGooglyEyes(g, c, c, r * 0.8);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Elite: a distinct spiky star with a little crown — rarer and tougher. */
  private makeElite(key: string, def: PathogenDef): void {
    const r = def.radius;
    const size = r * 2 + 18;
    const c = size / 2;
    const g = this.add.graphics();
    // Star burst body (more, sharper spikes than the virus).
    const points = 12;
    g.fillStyle(def.color, 1);
    for (let i = 0; i < points; i++) {
      const a = (i / points) * Math.PI * 2;
      g.fillCircle(c + Math.cos(a) * r, c + Math.sin(a) * r, r * 0.22);
    }
    g.fillStyle(def.color, 1);
    g.fillCircle(c, c, r * 0.82);
    g.lineStyle(3, 0xffffff, 0.5);
    g.strokeCircle(c, c, r * 0.82);
    // Crown of three little triangles on top.
    g.fillStyle(0xffe27a, 1);
    for (const off of [-1, 0, 1]) {
      const bx = c + off * r * 0.5;
      const by = c - r * 0.78;
      g.fillTriangle(bx - r * 0.16, by, bx + r * 0.16, by, bx, by - r * 0.4);
    }
    this.drawGooglyEyes(g, c, c, r * 0.82);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Two white eyes with off-center pupils — the gentle comedic personality. */
  private drawGooglyEyes(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    bodyR: number,
  ): void {
    // Eyes sit high on the body so the centered Strength number stays readable.
    const eyeR = bodyR * 0.24;
    const dx = bodyR * 0.34;
    const ey = cy - bodyR * 0.52;
    for (const sign of [-1, 1]) {
      const ex = cx + sign * dx;
      g.fillStyle(0xffffff, 1);
      g.fillCircle(ex, ey, eyeR);
      g.fillStyle(0x1a1a22, 1);
      g.fillCircle(ex + eyeR * 0.25, ey + eyeR * 0.25, eyeR * 0.45);
    }
  }

  /** Small white-cell projectile. */
  private makeShot(key: string): void {
    const size = 22;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(c, c, c - 1);
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(c, c, c * 0.62);
    g.fillStyle(COLORS.cellCore, 0.9);
    g.fillCircle(c, c, c * 0.3);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Hostile projectile fired by enemies. */
  private makeEnemyShot(key: string): void {
    const size = 20;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(COLORS.enemyShot, 0.35);
    g.fillCircle(c, c, c - 1);
    g.fillStyle(COLORS.enemyShot, 1);
    g.fillCircle(c, c, c * 0.6);
    g.fillStyle(0xffe0c0, 0.95);
    g.fillCircle(c, c, c * 0.28);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  /** Rounded pickup token with a simple symbol. */
  private makePickup(key: string, color: number, symbol: "plus" | "shield" | "rapid"): void {
    const size = 48;
    const c = size / 2;
    const g = this.add.graphics();
    g.fillStyle(color, 0.25);
    g.fillCircle(c, c, c - 2);
    g.fillStyle(color, 1);
    g.fillRoundedRect(c - 14, c - 14, 28, 28, 9);
    g.fillStyle(0xffffff, 0.95);
    if (symbol === "plus") {
      // power plus sign
      g.fillRect(c - 3, c - 11, 6, 22);
      g.fillRect(c - 11, c - 3, 22, 6);
    } else if (symbol === "rapid") {
      // double chevron (fast fire)
      g.fillTriangle(c - 10, c - 8, c - 10, c + 8, c - 1, c);
      g.fillTriangle(c + 1, c - 8, c + 1, c + 8, c + 10, c);
    } else {
      // shield
      g.fillRoundedRect(c - 8, c - 9, 16, 12, 3);
      g.fillTriangle(c - 8, c + 2, c + 8, c + 2, c, c + 11);
    }
    g.generateTexture(key, size, size);
    g.destroy();
  }

  private makeRing(
    key: string,
    radius: number,
    color: number,
    strokeAlpha: number,
    fillAlpha: number,
  ): void {
    const size = radius * 2 + 8;
    const c = size / 2;
    const g = this.add.graphics();
    if (fillAlpha > 0) {
      g.fillStyle(color, fillAlpha);
      g.fillCircle(c, c, radius);
    }
    g.lineStyle(4, color, strokeAlpha);
    g.strokeCircle(c, c, radius);
    g.generateTexture(key, size, size);
    g.destroy();
  }
}
